/**
 * Base Adapter
 * Provides utilities for data transformation between API and application layer
 */

import type { ApiResponse } from '@/types';
import { sanitizeTextInput } from '@/lib/sanitize';

export class BaseAdapter {
  /**
   * Extracts data from API response
   */
  protected static extractData<T>(response: ApiResponse<T>): T {
    return response.data;
  }

  /**
   * Transforms dates from ISO strings to Date objects
   */
  protected static parseDate(dateString: string): Date {
    return new Date(dateString);
  }

  /**
   * Formats date to ISO string for API requests
   */
  protected static formatDate(date: Date): string {
    return date.toISOString();
  }

  /**
   * Strips HTML/script tags from a string value before sending to API.
   */
  protected static sanitizeString(value: string): string {
    return sanitizeTextInput(value);
  }

  /**
   * Removes undefined/null values from object and sanitizes string values
   * to strip any HTML/script injection attempts.
   */
  protected static sanitizePayload<T>(payload: T): T {
    if (typeof payload !== 'object' || payload === null) {
      return payload;
    }
    return Object.entries(payload).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null) {
        (acc as Record<string, unknown>)[key] =
          typeof value === 'string' ? sanitizeTextInput(value) : value;
      }
      return acc;
    }, {} as T);
  }
}
